/* eslint-disable max-lines */
import { comparer } from 'mobx';
import { AnalyticsStore, ContextStore, PageStore } from 'bernie-plugin-mobx';

import { codeSplit, Controller, FetchOptions, FetchPageOptions, merge } from 'bernie-core';
import { Localization } from 'bernie-l10n';
import { PageData } from 'bernie-http';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { getPackageType } from 'stores/multi-item-store-util';
import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'src/common/stores/page-layout/page-layout-store';
import { SearchCriteriaModel, SearchCriteriaUtils, singleUseQueryParams, untrackedQueryParams } from 'stores/models';

import { InfositePageName } from 'constants/pages';
import { MarketingChannels } from 'constants/marketing-channels';

import { getPageData } from 'src/common/utils/controller-utils/get-page-data';
import { getSeoData } from 'src/common/utils/controller-utils/get-seo-data';
import { getTrackingInfoForLogging } from 'utils/logging-utils';
import { meta } from 'utils/meta';
import { processClientSideDefaultRequestFields } from 'controllers/hotel/shared-controller-modules/utils';

import { CONTROLLER_EVENTS } from 'controllers/events/controller-events';

const { withoutKeys, withKeys } = merge;

export interface ContextWithSeoData extends ContextStore {
  seoData: {
    url: string;
    anvilRequest: {
      identifiers: {
        hotel: string;
      };
    };
  };
}

export class InfositeController implements Controller {
  public bundles = ['infosite'];
  // @ts-ignore
  public component = codeSplit(() => import(/* webpackChunkName: "infosite" */ 'views/hotel/infosite.tsx'));
  public pageId = `${InfositePageName.SA},H,30`;
  public path =
    '/:langOrLocale([a-z]{2}-[a-z]{2}|[a-z]{2}[^ho])?/:descriptor(.*[^ho])?/:prefix(.*.h|ho*)?:propertyId(p*\\d+[a-z]*[^./]):suffix(/?.*)?';
  public exact = false;
  public routeName = 'infosite';
  public routeData = {
    rfrrNamespace: 'HOT.HIS',
    pageName: InfositePageName.SA,
  };

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const context = options.context as ContextWithSeoData;

    return Promise.resolve({
      ...getSeoData(context),
      ...getPageData(getPackageType(options.query)),
    } as PageData);
  }

  // eslint-disable-next-line complexity
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.fetch received invalid FetchOptions');
    }

    const { isServer, stores, query, params, logger } = options;
    const fetchStartTime = Date.now();

    const context: ContextWithSeoData = stores.get<ContextWithSeoData>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const currentHotel: CurrentHotelStore = stores.get<CurrentHotelStore>('currentHotel');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const multiItemBusinessModel = multiItem && multiItem.cartInfo && multiItem.cartInfo.type;
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const pageLayoutStore: PageLayoutStore = stores.get<PageLayoutStore>('pageLayout');

    multiItem.isMixMatchEnabled = query.mixMatch === 'true';
    multiItem.buildContext(query);

    const { prevParams, ...nextParams } = params || { prevParams: undefined };

    // for a server side rendered PDP on Vrbo and Hcom - leverage the blacksmith /v2/urlBuild call in the flex catalyst plugin which requests
    // the canonical PDP url and expedia property id from from blacksmith. this removes the dependency on the expediaPropetyId url parameter
    // for SSR'd PDPs on Vrbo and Hcom
    if (
      context?.seoData?.anvilRequest?.identifiers?.hotel !== undefined &&
      context?.seoData?.anvilRequest?.identifiers?.hotel !== nextParams.propertyId
    ) {
      nextParams.propertyId = context?.seoData?.anvilRequest?.identifiers?.hotel;
    }

    // query params cleanup
    const singleUseCriteria: Record<string, string | string[]> = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...Object.keys(singleUseCriteria), 'prevQuery', 'l10n'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery: Record<string, string> = withoutKeys(untrackedKeys).merge({}, query);

    if (multiItemBusinessModel && nextQuery) {
      nextQuery.businessModelFilter = multiItemBusinessModel;
    }

    let newQuery = processClientSideDefaultRequestFields(isServer, currentHotel, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const l10n = new Localization(context.locale);
    currentHotel.searchCriteria = new SearchCriteriaModel(SearchCriteriaUtils.ensureCorrectTypes(currentHotel.searchCriteria), l10n);

    // Fetch Template details for React View Composer
    const pageLayoutTemplatePromise = pageLayoutStore.fetchPDP(nextParams.propertyId, context, multiItem.isPackageShoppingPath());

    const forceFetch = currentHotel.shouldFetch;

    if (forceFetch || !comparer.structural(nextParams, prevParams) || !comparer.structural(prevQuery, nextQuery)) {
      // TEMP FIX: check removal after 2018-05-01 with @dboerner
      this.removeRegionIdForSEMUrls(nextQuery);
      // =====================================================

      const parsedInfositeValues = SearchCriteriaUtils.ensureCorrectTypes(
        SearchCriteriaUtils.parseInfositeValues({ ...nextQuery }, l10n, context.localDateFormat)
      );
      const incomingSearchCriteria: any = new SearchCriteriaModel(parsedInfositeValues, l10n);

      const criteriaChanged = this.haveCriteriaChanged(currentHotel.searchCriteria, incomingSearchCriteria, Object.keys(MarketingChannels));

      // 33486_PWA_Large_Party_Mix_Match reset cart if rooms or dates criteria changes
      const mixMatchCriteriaChanged = this.hasRoomsOrDatesChanged(currentHotel.searchCriteria, incomingSearchCriteria);

      if (multiItem && multiItem.showMixMatchExperience && mixMatchCriteriaChanged) {
        multiItem.emptyCart(context).then(() => {
          if (analyticsStore) {
            analyticsStore.trackEvent('MixMatch.EmptyCart', 'MixMatch Cart Emptied', null, null, true);
          }
        });
      }

      if (forceFetch || criteriaChanged || this.hasPropertyChanged(prevParams, nextParams)) {
        currentHotel.setOfferData(withKeys(CurrentHotelStore.offerSearchKeys).merge({}, parsedInfositeValues));
        currentHotel.updateSearchCriteria(incomingSearchCriteria);
        currentHotel.setHotelId(nextParams.propertyId);
        currentHotel.clearPoiData();

        if (isServer) {
          return;
        }

        if (!page.seo) {
          const pageFetch = page.fetch(meta['hotel-infosite'], context.site, context.locale);
          await pageFetch;
        }
      }
    }

    await pageLayoutTemplatePromise;

    logger.logEvent(
      CONTROLLER_EVENTS.INFOSITE_CONTROLLER_FETCH,
      `fetchTime=${(Date.now() - fetchStartTime).toString()} ms`,
      `, isServer=${isServer}, ${getTrackingInfoForLogging(context)}`
    );

    newQuery = processClientSideDefaultRequestFields(isServer, currentHotel, nextQuery);
    if (newQuery) {
      return newQuery;
    }
  }

  public async willDeactivate(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.willDeactivate receieved an invalid StoreContainer');
    }

    const currentHotel: CurrentHotelStore = options.stores.get<CurrentHotelStore>('currentHotel');

    if (currentHotel.isFailure) {
      currentHotel.shouldFetch = true;
    }
  }

  private removeRegionIdForSEMUrls(query: any): any {
    // TODO: SEM regionId fix (do not remove until SEM URLs have been fixed)
    if (query && /google/gi.test(query.semcid)) {
      delete query.regionId;
    }
  }

  private hasPropertyChanged(prevParams: any, nextParams: any): boolean {
    if (prevParams && nextParams && prevParams.propertyId && nextParams.propertyId) {
      return prevParams.propertyId !== nextParams.propertyId;
    }

    return false;
  }

  private haveCriteriaChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel, ignoredKeys: string[]): boolean {
    if (Boolean(newModel) && !previousModel) {
      return true;
    }

    return !comparer.structural(previousModel.toJSON(ignoredKeys), newModel.toJSON(ignoredKeys));
  }

  private hasRoomsOrDatesChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel): boolean {
    if (newModel && !previousModel) {
      return true;
    }
    const startDateChanged = previousModel.startDate !== newModel.startDate;
    const endDateChanged = previousModel.endDate !== newModel.endDate;

    return !comparer.structural(previousModel.rooms, newModel.rooms) || startDateChanged || endDateChanged;
  }
}
